import { addError } from '@infra/datadog/tracking';
import { DeviceIdService } from '@services/DeviceIdService';
import { TimeSyncService } from '@services/TimeSyncService';
import createClient from 'openapi-fetch';
import type { paths, components } from '@api/club/generated';

import { ApiError } from '@libs/errors';
import { SignalRSocket, SocketOptions } from '@libs/socket-signalr';

import { AuthService } from '@features/auth/services/AuthService';

export { components, paths };

const NAME = 'ClubApi';

/*
    ____,-------------------------------,____
    \   |          Http Client          |   /
    /___|-------------------------------|___\
*/

const api = createClient<paths>({
  baseUrl: __RUNTIME.clubApi,
});

export class UnauthorizedError extends Error {
  constructor(
    public url: string,
    message: string,
  ) {
    super(`Unauthorized. Url: ${url}, message: ${message}`);
  }
  name = 'UnauthorizedError';
}

api.use({
  async onRequest({ request }) {
    request.headers.set('X-Device-Id', DeviceIdService.instance.id);

    // SEE: https://openapi-ts.dev/openapi-fetch/middleware-auth
    if (!request.headers.has('Authorization') && AuthService.instance.accessToken) {
      request.headers.set('Authorization', `Bearer ${AuthService.instance.accessToken}`);
    }
    return request;
  },

  async onResponse({ request, response }) {
    // sic! если токен есть — он невалидный и надо его стереть
    if (response.status === 401 && isCurrentToken(request)) {
      AuthService.instance.deleteTokens();
    }

    if (response.status >= 400) {
      const err = await ApiError.fromResponse(NAME, response);
      const isInvalidPassword = err.isUnauthorized && response.url.includes('/auth/login');

      // не логируем неверные логины
      if (!isInvalidPassword) {
        addError(err);
      }
      throw err;
    }
  },

  onError({ error, request }) {
    const err = ApiError.fromRequestError(NAME, request, error);
    addError(err);
    throw err;
  },
});

const { GET, POST, DELETE } = api;
export { GET, POST, DELETE };

/*
    ____,-------------------------------,____
    \   |         Socket Clients        |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// Club Socket: события клуба. Три хаба с одним набором событий: личный, клубный стрим
// без входа и менеджерский — он отдает те же события плюс события менеджерам, поэтому
// менеджер держит только его

export const USER_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/user`;
export const MANAGER_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/manager`;

export const managerSocketUrl = (clubId: string) => `${MANAGER_SOCKET_URL}?clubId=${clubId}`;

export type UserSocketEvents =
  // только свои
  | components['schemas']['GameStartsSoon']
  | components['schemas']['GameStarted']
  | components['schemas']['GameFinished']
  | components['schemas']['NoPair']
  | components['schemas']['SeanceStartsSoon']
  | components['schemas']['SeanceStarted']
  // все
  | components['schemas']['TournamentStartsSoon']
  | components['schemas']['RoundDrafted']
  | components['schemas']['RoundStarted']
  | components['schemas']['RoundFinished']
  | components['schemas']['TournamentPublished']
  | components['schemas']['TournamentUpdated']
  | components['schemas']['TournamentFinished']
  | components['schemas']['ParticipantsUpdated']
  | components['schemas']['ParticipantWasBanned']
  | components['schemas']['TeamWasBanned']
  | components['schemas']['ParticipantWasRemoved']
  // видеоконтроль: флаг приватной комнаты своего участника
  | components['schemas']['ParticipantAuxUpdated']
  // настройки участника клуба (число камер для видеоконтроля)
  | components['schemas']['MemberAuxUpdated'];

// только менеджерам клуба
export type ManagerSocketEvents =
  | components['schemas']['ClubParticipantAuxUpdated']
  | components['schemas']['ClubMemberAuxUpdated']
  // видеокомнаты: турниру выделены комнаты, участнику выдан билет и назначена комната
  | components['schemas']['ClubTournamentVideoRoomsAllocated']
  | components['schemas']['ClubParticipantVideoRoomSet'];

export type ClubSocketEvents = UserSocketEvents | ManagerSocketEvents;

export class ClubSocket extends SignalRSocket<ClubSocketEvents> {
  constructor(public url: string = USER_SOCKET_URL) {
    const EVENT_TYPES: Record<ClubSocketEvents['eventType'], null> = {
      GameStartsSoon: null,
      GameStarted: null,
      GameFinished: null,
      NoPair: null,
      SeanceStartsSoon: null,
      SeanceStarted: null,
      //
      TournamentStartsSoon: null,
      RoundDrafted: null,
      RoundStarted: null,
      RoundFinished: null,
      TournamentPublished: null,
      TournamentUpdated: null,
      TournamentFinished: null,
      ParticipantsUpdated: null,
      ParticipantWasBanned: null,
      TeamWasBanned: null,
      ParticipantWasRemoved: null,
      ParticipantAuxUpdated: null,
      MemberAuxUpdated: null,
      //
      ClubParticipantAuxUpdated: null,
      ClubMemberAuxUpdated: null,
      ClubTournamentVideoRoomsAllocated: null,
      ClubParticipantVideoRoomSet: null,
    };

    const isPersonal = url.startsWith(USER_SOCKET_URL);
    const isManager = url.startsWith(MANAGER_SOCKET_URL);

    const options: SocketOptions<ClubSocketEvents> = {
      accessTokenFactory:
        isPersonal || isManager ? AuthService.instance.socketAccessTokenFactory : undefined,

      onMsg: (e) => {
        // у событий aux и видеокомнаты метки времени нет
        if ('timestampMs' in e) TimeSyncService.instance.reportSocketTs(e.timestampMs);
      },
    };

    const u = new URL(url);

    // менеджерский хаб знает пользователя по токену
    if (!isManager && AuthService.instance.user?.userId) {
      u.searchParams.append('userId', AuthService.instance.user?.userId);
    }

    super(u.toString(), Object.keys(EVENT_TYPES), options);

    // «я на месте» принимают личный и менеджерский хабы, клубный стрим без входа — нет
    if (isPersonal || isManager) {
      this.startOnlineReporting();
    }
  }

  startOnlineReporting() {
    this.subscribe((e) => {
      if (e.eventType === 'TournamentStartsSoon') {
        this.connection.send('IAmOnline', new Date().toISOString());
      }
    });
  }
}

// ------------------------------------------
// Tournament Video Socket: телефон участника, вход по видеобилету вместо JWT сайта

export const TOURNAMENT_VIDEO_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/tournament-video`;

export type TournamentVideoSocketEvents = components['schemas']['ParticipantAuxUpdated'];

export class TournamentVideoSocket extends SignalRSocket<TournamentVideoSocketEvents> {
  constructor(
    public tournamentId: string,
    ticket: string,
  ) {
    const EVENT_TYPES: Record<TournamentVideoSocketEvents['eventType'], null> = {
      ParticipantAuxUpdated: null,
    };

    super(`${TOURNAMENT_VIDEO_SOCKET_URL}/${tournamentId}`, Object.keys(EVENT_TYPES), {
      accessTokenFactory: () => ticket,
    });
  }
}

function isCurrentToken(request: Request): boolean {
  const token = AuthService.instance.accessToken;
  return !!token && request.headers.get('Authorization') === `Bearer ${token}`;
}
